import React, { useMemo, useRef, useState } from "react";

const DEFAULT_INNER_GRADIENT =
  "linear-gradient(145deg,#1e293bcc 0%,#0f172a 45%,#155e75aa 100%)";

const clamp = (v, min = -18, max = 18) => Math.min(Math.max(v, min), max);

const ProfileCard = ({
  name = "Chetan Jindal",
  title = "Full Stack Developer",
  handle = "chetanjindal",
  status = "Available for Work",
  contactText = "Contact Me",
  avatarUrl = "",
  showUserInfo = true,
  enableTilt = true,
  onContactClick,
  behindGlowColor = "rgba(125,190,255,0.65)",
  innerGradient = DEFAULT_INNER_GRADIENT,
}) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, glowX: "50%", glowY: "50%" });

  const initials = useMemo(
    () =>
      name
        .split(" ")
        .slice(0, 2)
        .map((n) => n[0])
        .join("")
        .toUpperCase(),
    [name]
  );

  const handleMove = (event) => {
    if (!enableTilt || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;

    const rx = clamp((0.5 - py) * 16);
    const ry = clamp((px - 0.5) * 18);

    setTilt({ rx, ry, glowX: `${px * 100}%`, glowY: `${py * 100}%` });
  };

  const resetTilt = () => setTilt({ rx: 0, ry: 0, glowX: "50%", glowY: "50%" });

  return (
    <div className="mx-auto w-full max-w-sm">
      {/* animated border */}
      <div className="relative rounded-[28px] p-[1px]">
        <div className="absolute inset-0 rounded-[28px] bg-gradient-to-r from-cyan-400/40 via-blue-500/40 to-emerald-400/40 animate-[spin_12s_linear_infinite]" />

        <div
          ref={cardRef}
          onMouseMove={handleMove}
          onMouseLeave={resetTilt}
          className="relative overflow-hidden rounded-[28px] bg-slate-900/70 p-5"
          style={{
            transform: enableTilt
              ? `perspective(900px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`
              : "none",
            backgroundImage: innerGradient,
          }}
        >
          {/* glow behind */}
          <div
            className="pointer-events-none absolute -inset-1 blur-2xl"
            style={{
              background: `radial-gradient(circle at ${tilt.glowX} ${tilt.glowY}, ${behindGlowColor} 0%, transparent 60%)`,
              opacity: 0.7,
            }}
          />

          {/* shine sweep */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-1/2 top-0 h-full w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 animate-[shine_1.5s]" />
          </div>

          <div className="relative z-10 rounded-2xl border border-white/10 bg-slate-950/40 p-4 backdrop-blur-xl text-center">
            {/* avatar */}
            <div className="group relative mx-auto mb-4 flex h-32 w-32 items-center justify-center overflow-hidden rounded-2xl border border-cyan-300/35 bg-slate-900">
              <div className="absolute inset-0 rounded-2xl bg-cyan-400/20 blur-xl opacity-60 animate-pulse" />

              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={name}
                  className="relative h-full w-full object-cover object-top scale-110"
                />
              ) : (
                <span className="text-2xl font-bold text-cyan-200">{initials}</span>
              )}
            </div>

            <h3 className="text-2xl font-bold text-white">{name}</h3>
            <p className="text-sm text-cyan-200">{title}</p>

            {showUserInfo && (
              <div className="mt-4 rounded-xl border border-white/10 bg-slate-900/40 p-3">
                <p className="text-sm text-slate-200">@{handle}</p>
                <p className="mt-1 text-xs text-emerald-300">{status}</p>
              </div>
            )}

            <button
              onClick={() => onContactClick?.()}
              className="mt-4 w-full rounded-xl border border-cyan-300/45 bg-cyan-300/10 px-4 py-3 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-300/20"
            >
              {contactText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
